/**
 * Servicio de reportes y análisis financiero.
 * Requiere cliente autenticado de Supabase como primer parámetro.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { VistaSaldoCliente } from '@/types/database'
import { fetchAllPages } from '@/lib/postgrest-chunks'

export interface ClienteDeudor extends VistaSaldoCliente {
  diasSinPago?: number
  ultimoPago?: string | null
}

export type TopDeudor = Pick<VistaSaldoCliente, 'id' | 'nombre' | 'saldo'>

export const reportesService = {
  /**
   * Clientes de la organización con saldo positivo, con la fecha de su último pago.
   * Los pagos de la organización se leen en una sola consulta paginada y se agrupan por cliente.
   */
  async obtenerDeudores(client: SupabaseClient<Database>, organizacionId: string, filtros?: {
    minDeuda?: number
    maxDeuda?: number
    estado?: string
  }): Promise<ClienteDeudor[]> {

    let query = client
      .from('vista_saldos_clientes')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .gt('saldo', 0)
      .order('saldo', { ascending: false })

    if (filtros?.minDeuda) {
      query = query.gte('saldo', filtros.minDeuda)
    }
    if (filtros?.maxDeuda) {
      query = query.lte('saldo', filtros.maxDeuda)
    }
    if (filtros?.estado) {
      query = query.eq('estado', filtros.estado)
    }

    const { data: clientes, error } = await query

    if (error) throw error

    const clientesData = (clientes || []) as VistaSaldoCliente[]
    if (clientesData.length === 0) return []

    const pagos = await fetchAllPages<{ cliente_id: string | null; fecha: string }>(async (desde, hasta) => {
      const { data, error } = await client
        .from('movimientos')
        .select('cliente_id, fecha')
        .eq('organizacion_id', organizacionId)
        .eq('tipo', 'Pago')
        .order('fecha', { ascending: false })
        .order('id')
        .range(desde, hasta)
      if (error) throw error
      return (data ?? []) as { cliente_id: string | null; fecha: string }[]
    })

    // Los pagos llegan del más nuevo al más viejo: el primero de cada cliente es su último pago.
    const ultimoPagoPorCliente = new Map<string, string>()
    for (const pago of pagos) {
      if (pago.cliente_id && !ultimoPagoPorCliente.has(pago.cliente_id)) {
        ultimoPagoPorCliente.set(pago.cliente_id, pago.fecha)
      }
    }

    return clientesData.map((cliente) => {
      const ultimoPago = ultimoPagoPorCliente.get(cliente.id ?? '') ?? null
      const diasSinPago = ultimoPago
        ? Math.floor((Date.now() - new Date(ultimoPago).getTime()) / (1000 * 60 * 60 * 24))
        : undefined
      return { ...cliente, ultimoPago, diasSinPago }
    })
  },

  /** Los clientes activos de la organización con mayor saldo, y cuántos activos tienen saldo positivo. */
  async obtenerTopDeudores(
    client: SupabaseClient<Database>,
    organizacionId: string,
    limite: number,
  ): Promise<{ deudores: TopDeudor[]; total: number }> {
    const { data, error, count } = await client
      .from('vista_saldos_clientes')
      .select('id, nombre, saldo', { count: 'exact' })
      .eq('organizacion_id', organizacionId)
      .eq('estado', 'activo')
      .gt('saldo', 0)
      .order('saldo', { ascending: false })
      .limit(limite)

    if (error) throw error
    return { deudores: (data ?? []) as TopDeudor[], total: count ?? 0 }
  },

  // Exportar deudas a CSV
  generarCSVDeudas(deudores: ClienteDeudor[]): string {
    const headers = ['Cliente', 'CUIT', 'Email', 'Teléfono', 'Responsable', 'Saldo', 'Último Pago', 'Días sin Pago']
    const rows = deudores.map((d) => [
      d.nombre,
      d.cuit?.toString() || '',
      d.email || '',
      d.telefono || '',
      d.responsable || '',
      d.saldo?.toFixed(2) || '0',
      d.ultimoPago || 'Sin pagos',
      d.diasSinPago?.toString() || '-',
    ])

    return [headers, ...rows].map((row) => row.map(cell => `"${cell}"`).join(';')).join('\n')
  },
}
